// Product Mapping master list (Kalpana UAT round 4 — DMS-20260428).
// Replaces AdminValidation. One row = (Indian Brand, Generic, Container).
// Bulk upload from CSV + sample download.

import { useEffect, useState } from 'react';
import { productMappingApi, downloadAuthed } from '../api/services';
import toast from 'react-hot-toast';
import {
  Plus, Loader2, X, Upload, Download, Search, Edit2, Save,
} from 'lucide-react';

const PAGE_SIZE = 100;

const PACK_FORMS = ['strip', 'bottle', 'tube', 'inhaler', 'sachet', 'cream', 'drop', 'injection', 'soap', 'powder', 'other'];
const PACK_UNITS = ['pills', 'ml', 'g', 'doses', 'units'];

function fmtPack(m) {
  if (!m.pack_form) return null;
  const size = m.pack_size != null ? Number(m.pack_size) : null;
  // "tube of 1 units" reads silly — a pack of one is just the form itself.
  if (!size || size <= 1) return m.pack_form;
  const unit = m.pack_unit && m.pack_unit !== 'pills' ? ` ${m.pack_unit}` : '';
  return `${m.pack_form} of ${size}${unit}`;
}

export default function AdminProductMapping() {
  const [mappings, setMappings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Add new mapping form
  const [form, setForm] = useState({ indianBrand: '', genericName: '', container: '', packForm: '', packSize: '', packUnit: '' });
  const [adding, setAdding] = useState(false);

  // Bulk upload state
  const [bulkFile, setBulkFile] = useState(null);
  const [mboFile, setMboFile] = useState(null);
  const [importingMbo, setImportingMbo] = useState(false);
  const [mboResult, setMboResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  async function load(s = search, p = page) {
    setLoading(true);
    try {
      const res = await productMappingApi.list({
        search: s,
        limit: PAGE_SIZE,
        offset: p * PAGE_SIZE,
      });
      setMappings(res.data.mappings || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load mappings');
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  async function add() {
    if (!form.indianBrand.trim() || !form.genericName.trim()) {
      toast.error('Indian Brand and Generic Name are required'); return;
    }
    setAdding(true);
    try {
      await productMappingApi.add({
        indianBrand: form.indianBrand.trim(),
        genericName: form.genericName.trim(),
        container: form.container.trim() || null,
        packForm: form.packForm || null,
        packSize: form.packSize || null,
        packUnit: form.packUnit || null,
      });
      toast.success('Added');
      setForm({ indianBrand: '', genericName: '', container: '', packForm: '', packSize: '', packUnit: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setAdding(false); }
  }

  async function remove(id) {
    if (!window.confirm('Remove this mapping?')) return;
    try {
      await productMappingApi.remove(id);
      toast.success('Removed');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function startEdit(m) {
    setEditId(m.id);
    setEditForm({
      indianBrand: m.indian_brand,
      genericName: m.generic_name,
      container: m.container || '',
      packForm: m.pack_form || '',
      packSize: m.pack_size != null ? Number(m.pack_size) : '',
      packUnit: m.pack_unit || '',
    });
  }

  async function saveEdit() {
    try {
      await productMappingApi.update(editId, {
        indianBrand: editForm.indianBrand.trim(),
        genericName: editForm.genericName.trim(),
        container: editForm.container.trim() || null,
        packForm: editForm.packForm || '',
        packSize: editForm.packSize || '',
        packUnit: editForm.packUnit || '',
      });
      toast.success('Saved');
      setEditId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function uploadBulk() {
    if (!bulkFile) { toast.error('Pick a CSV first'); return; }
    setUploading(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const res = await productMappingApi.bulkUpload(fd);
      setBulkResult(res.data);
      toast.success(`Inserted ${res.data.inserted}, updated ${res.data.updated}, skipped ${res.data.skipped.length}`);
      setBulkFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk upload failed');
    } finally { setUploading(false); }
  }

  async function uploadMboWorkbook() {
    if (!mboFile) { toast.error('Pick the XLSX file first'); return; }
    setImportingMbo(true); setMboResult(null);
    try {
      const fd = new FormData();
      fd.append('file', mboFile);
      const res = await productMappingApi.bulkUploadMasterFromMbo(fd);
      setMboResult(res.data);
      const { total } = res.data;
      toast.success(`Imported ${total.parsed} rows: ${total.added} new, ${total.updated} updated, ${total.skipped} skipped`);
      setMboFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'MBO workbook import failed');
    } finally { setImportingMbo(false); }
  }

  function runSearch() {
    setSearch(searchInput);
    setPage(0);
    load(searchInput, 0);
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <p className="text-sm text-gray-500 mb-4">
        Master list of Indian Brand → Generic Name → Container mappings.
        Validates new MBO uploads — flagged orders are routed to Dispatch
        Admin for Approve / Reject / Skip decisions. Empty list = no
        validation runs (graceful, until you populate the master).
      </p>

      {/* Bulk upload + sample */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="text-sm font-semibold mb-2">Bulk upload</div>
        <div className="flex flex-wrap items-end gap-2">
          <input type="file" accept=".csv"
                 onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                 className="text-sm" />
          <button onClick={uploadBulk} disabled={!bulkFile || uploading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload CSV
          </button>
          <button onClick={() => downloadAuthed(productMappingApi.sampleUrl().replace('/api', ''),
                                                 'product-mapping-sample.csv').catch(() =>
                  toast.error('Sample download failed'))}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            <Download className="w-4 h-4" /> Download sample
          </button>
          <button onClick={() => downloadAuthed(productMappingApi.exportUrl().replace('/api', ''),
                                                 `product-mapping-${new Date().toISOString().slice(0,10)}.csv`).catch(() =>
                  toast.error('Export failed'))}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            <Download className="w-4 h-4" /> Export current data
          </button>
          <span className="text-xs text-gray-500 ml-auto">
            CSV columns: <code>indian_brand, generic_name, container, pack_form, pack_size, pack_unit</code> (all but the first two optional)
          </span>
        </div>
        {bulkResult && (
          <div className="mt-3 text-sm">
            <div className="text-green-700 font-semibold">
              ✓ Inserted {bulkResult.inserted} new, updated {bulkResult.updated} existing.
            </div>
            {bulkResult.skipped.length > 0 && (
              <details className="mt-2 text-xs text-amber-700">
                <summary>{bulkResult.skipped.length} rows skipped</summary>
                <ul className="mt-1 ml-4 list-disc">
                  {bulkResult.skipped.slice(0, 50).map((s, i) => (
                    <li key={i}>Row {s.row_num}: {s.reason}</li>
                  ))}
                  {bulkResult.skipped.length > 50 && <li>…and {bulkResult.skipped.length - 50} more</li>}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Import from IDM/TKS workbook — Phase 3 of the product-mapping bridge */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="text-sm font-semibold mb-1">Import from MBO workbook (IDM + TKS)</div>
        <p className="text-xs text-gray-500 mb-3">
          Upload the <code>IDM and TKS product details.xlsx</code> workbook Kalpana maintains. Both
          sheets are read in one pass: IDM uses <em>Indian Brand</em> + <em>Product Name (Generic)</em>,
          TKS uses <em>Indian Name</em> + <em>Display Name</em>. Existing master rows are updated;
          new ones are added.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <input type="file" accept=".xlsx,.xls"
                 onChange={(e) => setMboFile(e.target.files?.[0] || null)}
                 className="text-sm" />
          <button onClick={uploadMboWorkbook} disabled={!mboFile || importingMbo}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {importingMbo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Import workbook
          </button>
          <button onClick={() => downloadAuthed(
                    productMappingApi.sampleMboXlsxUrl().replace('/api', ''),
                    'IDM-and-TKS-product-details-sample.xlsx',
                  ).catch(() => toast.error('Sample download failed'))}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            <Download className="w-4 h-4" /> Download sample workbook
          </button>
        </div>
        {mboResult && (
          <div className="mt-3 text-sm">
            <div className="text-green-700 font-semibold">
              ✓ Imported {mboResult.total.parsed} rows total — {mboResult.total.added} new, {mboResult.total.updated} updated, {mboResult.total.skipped} skipped.
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {mboResult.sheets.map((s) => (
                <div key={s.name} className="border border-gray-200 rounded p-2 text-xs">
                  <div className="font-semibold text-gray-700">{s.name}</div>
                  <div className="text-gray-600 mt-0.5">
                    {s.parsed} parsed · {s.added} new · {s.updated} updated · {s.skipped} skipped
                  </div>
                  {s.errors?.length > 0 && (
                    <details className="mt-1 text-amber-700">
                      <summary>{s.errors.length} note(s)</summary>
                      <ul className="mt-1 ml-3 list-disc">
                        {s.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                        {s.errors.length > 20 && <li>…and {s.errors.length - 20} more</li>}
                      </ul>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add single + search */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="text-sm font-semibold mb-2">Add single mapping</div>
        <div className="flex gap-2 items-end flex-wrap mb-3">
          <label className="flex-1 min-w-[180px]">
            <span className="block text-xs text-gray-500 mb-1">Indian Brand</span>
            <input value={form.indianBrand}
                   onChange={(e) => setForm({ ...form, indianBrand: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="flex-1 min-w-[180px]">
            <span className="block text-xs text-gray-500 mb-1">Generic Name</span>
            <input value={form.genericName}
                   onChange={(e) => setForm({ ...form, genericName: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="w-28">
            <span className="block text-xs text-gray-500 mb-1">Form</span>
            <select value={form.packForm}
                    onChange={(e) => setForm({ ...form, packForm: e.target.value })}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">—</option>
              {PACK_FORMS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="w-24">
            <span className="block text-xs text-gray-500 mb-1">Pack size</span>
            <input type="number" min="0" step="any" value={form.packSize}
                   onChange={(e) => setForm({ ...form, packSize: e.target.value })}
                   placeholder="10"
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="w-24">
            <span className="block text-xs text-gray-500 mb-1">Unit</span>
            <select value={form.packUnit}
                    onChange={(e) => setForm({ ...form, packUnit: e.target.value })}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">—</option>
              {PACK_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          <button onClick={add} disabled={adding}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
        </div>
      </div>

      {/* Search + list */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400" />
          <input value={searchInput}
                 onChange={(e) => setSearchInput(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                 placeholder="Search brand or generic…"
                 className="flex-1 px-2 py-1 text-sm focus:outline-none" />
          <button onClick={runSearch}
                  className="text-sm text-blue-600 hover:underline">Search</button>
          <span className="text-xs text-gray-500 ml-auto">{total} total</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
          </div>
        ) : mappings.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No mappings. Bulk upload or add manually above.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Indian Brand</th>
                <th className="text-left px-4 py-2">Generic Name</th>
                <th className="text-left px-4 py-2">Pack</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mappings.map((m) => editId === m.id ? (
                <tr key={m.id} className="bg-blue-50">
                  <td className="px-4 py-2">
                    <input value={editForm.indianBrand}
                           onChange={(e) => setEditForm({ ...editForm, indianBrand: e.target.value })}
                           className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                  </td>
                  <td className="px-4 py-2">
                    <input value={editForm.genericName}
                           onChange={(e) => setEditForm({ ...editForm, genericName: e.target.value })}
                           className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <select value={editForm.packForm}
                              onChange={(e) => setEditForm({ ...editForm, packForm: e.target.value })}
                              className="px-1 py-1 border border-gray-300 rounded text-xs">
                        <option value="">—</option>
                        {PACK_FORMS.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <input type="number" min="0" step="any" value={editForm.packSize}
                             onChange={(e) => setEditForm({ ...editForm, packSize: e.target.value })}
                             placeholder="size"
                             className="w-16 px-1 py-1 border border-gray-300 rounded text-xs" />
                      <select value={editForm.packUnit}
                              onChange={(e) => setEditForm({ ...editForm, packUnit: e.target.value })}
                              className="px-1 py-1 border border-gray-300 rounded text-xs">
                        <option value="">—</option>
                        {PACK_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={saveEdit} className="text-blue-600 hover:underline mr-2 text-xs">
                      <Save className="w-3.5 h-3.5 inline" /> Save
                    </button>
                    <button onClick={() => setEditId(null)} className="text-gray-500 text-xs">Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{m.indian_brand}</td>
                  <td className="px-4 py-2">{m.generic_name}</td>
                  <td className="px-4 py-2 text-sm">
                    {fmtPack(m)
                      ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">{fmtPack(m)}</span>
                      : <span className="text-gray-300">not set</span>}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(m)}
                            className="text-gray-400 hover:text-blue-600 mr-2"
                            title="Edit">
                      <Edit2 className="w-4 h-4 inline" />
                    </button>
                    <button onClick={() => remove(m.id)}
                            className="text-gray-400 hover:text-red-600"
                            title="Remove">
                      <X className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {total > 0 && (
          <div className="flex items-center justify-between mt-3 text-xs text-gray-600">
            <span>
              Showing <strong>{mappings.length === 0 ? 0 : (page * PAGE_SIZE) + 1}</strong>
              {'–'}
              <strong>{(page * PAGE_SIZE) + mappings.length}</strong>
              {' of '}
              <strong>{total}</strong>
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(0)}
                      disabled={page === 0}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
                « First
              </button>
              <button onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
                ‹ Prev
              </button>
              <span className="px-2">
                Page <strong>{page + 1}</strong> of <strong>{Math.max(1, Math.ceil(total / PAGE_SIZE))}</strong>
              </span>
              <button onClick={() => setPage((p) => p + 1)}
                      disabled={(page + 1) * PAGE_SIZE >= total}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
                Next ›
              </button>
              <button onClick={() => setPage(Math.max(0, Math.ceil(total / PAGE_SIZE) - 1))}
                      disabled={(page + 1) * PAGE_SIZE >= total}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
                Last »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
